import React from 'react'
import moment from 'moment'
import {Col, Row} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {NodeContact} from '../../../../types/contactsApi'
import {PhoneProviderActions} from '../../../../components/telephony/PhoneProviderActions'

const Field: React.FC<{label: React.ReactNode; children?: React.ReactNode}> = ({
  label,
  children,
}) => (
  <div className='mb-3'>
    <div className='fw-bold mb-1'>{label}</div>
    <div className='text-dark'>{children}</div>
  </div>
)

interface ContactPersonalTabProps {
  contact: NodeContact
}

export const ContactPersonalTab: React.FC<ContactPersonalTabProps> = ({contact}) => {
  const intl = useIntl()
  const na = intl.formatMessage({id: 'common.value.notAvailable', defaultMessage: 'N/A'})
  const value = (raw?: string | null) => (raw && `${raw}`.trim() !== '' ? raw : na)

  return (
    <div className='policy-form-tabs-container'>
      <div className='kt-heading kt-heading--md custom-border-brand'>
        <FormattedMessage
          id='contacts.profile.personal.title'
          defaultMessage='Personal Information'
        />
      </div>
      <Row>
        <Col md={3}>
          <Field
            label={
              <FormattedMessage id='contacts.profile.field.fullName' defaultMessage='Full Name' />
            }
          >
            {value(contact.fullName)}
          </Field>
        </Col>
        <Col md={3}>
          <Field
            label={
              <FormattedMessage
                id='common.field.dateOfBirth'
                defaultMessage='Date of Birth'
              />
            }
          >
            {contact.dateBirth ? moment(contact.dateBirth).format('MM/DD/YYYY') : na}
          </Field>
        </Col>
        <Col md={3}>
          <Field
            label={<FormattedMessage id='common.field.email' defaultMessage='Email' />}
          >
            {value(contact.email)}
          </Field>
        </Col>
        <Col md={3}>
          <Field
            label={<FormattedMessage id='common.field.phone' defaultMessage='Phone' />}
          >
            <PhoneProviderActions phone={contact.phone} emptyText={na} />
          </Field>
        </Col>
      </Row>
      <Row>
        <Col md={3}>
          <Field
            label={
              <FormattedMessage id='common.field.firstName' defaultMessage='First Name' />
            }
          >
            {value(contact.firstName)}
          </Field>
        </Col>
        <Col md={3}>
          <Field
            label={
              <FormattedMessage
                id='common.field.middleName'
                defaultMessage='Middle Name'
              />
            }
          >
            {value(contact.middleName)}
          </Field>
        </Col>
        <Col md={3}>
          <Field
            label={
              <FormattedMessage id='common.field.lastName' defaultMessage='Last Name' />
            }
          >
            {value(contact.lastName)}
          </Field>
        </Col>
        <Col md={3}>
          <Field
            label={
              <FormattedMessage
                id='common.field.secondLastName'
                defaultMessage='Second Last Name'
              />
            }
          >
            {value(contact.secondLastName)}
          </Field>
        </Col>
      </Row>
      <Row>
        <Col md={3}>
          <Field
            label={<FormattedMessage id='common.field.gender' defaultMessage='Gender' />}
          >
            {value(contact.gender)}
          </Field>
        </Col>
        <Col md={3}>
          <Field
            label={
              <FormattedMessage
                id='contacts.profile.field.preferredLanguage'
                defaultMessage='Preferred Language'
              />
            }
          >
            {value(contact.preferredLenguage)}
          </Field>
        </Col>
        <Col md={3}>
          <Field
            label={
              <FormattedMessage
                id='contacts.profile.field.immigrationStatus'
                defaultMessage='Immigration Status'
              />
            }
          >
            {value(contact.immigrationStatus)}
          </Field>
        </Col>
        <Col md={3}>
          <Field
            label={
              <FormattedMessage
                id='contacts.profile.field.subscriptionStatus'
                defaultMessage='Subscription Status'
              />
            }
          >
            {value(contact.subscriptionStatus)}
          </Field>
        </Col>
      </Row>

      <div className='kt-heading kt-heading--md custom-border-brand'>
        <FormattedMessage
          id='contacts.profile.address.title'
          defaultMessage='Address Information'
        />
      </div>
      <Row>
        <Col md={4}>
          <Field
            label={
              <FormattedMessage id='contacts.profile.field.address1' defaultMessage='Address 1' />
            }
          >
            {value(contact.address1)}
          </Field>
        </Col>
        <Col md={4}>
          <Field
            label={
              <FormattedMessage id='contacts.profile.field.address2' defaultMessage='Address 2' />
            }
          >
            {value(contact.address2)}
          </Field>
        </Col>
        <Col md={4}>
          <Field
            label={<FormattedMessage id='common.field.city' defaultMessage='City' />}
          >
            {value(contact.city)}
          </Field>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <Field
            label={<FormattedMessage id='common.field.state' defaultMessage='State' />}
          >
            {value(contact.stateEU?.name)}
          </Field>
        </Col>
        <Col md={4}>
          <Field
            label={
              <FormattedMessage id='common.field.zipCode' defaultMessage='Zip Code' />
            }
          >
            {value(contact.zipCode)}
          </Field>
        </Col>
        <Col md={4}>
          <Field
            label={
              <FormattedMessage
                id='contacts.profile.field.countyFips'
                defaultMessage='County FIPS'
              />
            }
          >
            {value(contact.countyFips)}
          </Field>
        </Col>
      </Row>

      <div className='kt-heading kt-heading--md custom-border-brand'>
        <FormattedMessage
          id='contacts.profile.additional.title'
          defaultMessage='Additional Information'
        />
      </div>
      <Row>
        <Col md={3}>
          <Field
            label={
              <FormattedMessage
                id='contacts.profile.field.annualIncome1'
                defaultMessage='Annual Income 1'
              />
            }
          >
            {value(contact.annualIncome1)}
          </Field>
        </Col>
        <Col md={3}>
          <Field
            label={
              <FormattedMessage
                id='contacts.profile.field.annualIncome2'
                defaultMessage='Annual Income 2'
              />
            }
          >
            {value(contact.annualIncome2)}
          </Field>
        </Col>
        <Col md={3}>
          <Field
            label={
              <FormattedMessage
                id='contacts.profile.field.annualIncome3'
                defaultMessage='Annual Income 3'
              />
            }
          >
            {value(contact.annualIncome3)}
          </Field>
        </Col>
        <Col md={3}>
          <Field
            label={<FormattedMessage id='contacts.profile.field.lead' defaultMessage='Lead' />}
          >
            {value(contact.lead?.name)}
          </Field>
        </Col>
      </Row>
      <Row>
        <Col md={3}>
          <Field
            label={
              <FormattedMessage id='common.field.created' defaultMessage='Created' />
            }
          >
            {contact.created ? moment(contact.created).format('MMM DD, YYYY hh:mm A') : na}
          </Field>
        </Col>
        <Col md={3}>
          <Field
            label={
              <FormattedMessage
                id='contacts.profile.field.updatedAt'
                defaultMessage='Last Update'
              />
            }
          >
            {contact.updated ? moment(contact.updated).format('MMM DD, YYYY hh:mm A') : na}
          </Field>
        </Col>
      </Row>
    </div>
  )
}
